import multiFileLayoutTemplate from './multi-file-layout.tpl.html';
import multiFileLayoutTabsetTemplate from './multi-file-layout-tabset.tpl.html';
import fileTabTemplateCodeEditor from './file-tab-templates/code-editor.tpl.html';
import fileTabTemplateMarkdownEditor from './file-tab-templates/markdown-editor.tpl.html';
import fileTabTemplateMarkdownViewer from './file-tab-templates/markdown-viewer.tpl.html';
import fileTabTemplateImageViewer from './file-tab-templates/image-viewer.tpl.html';
import fileTabTemplateInstructionsViewer from './file-tab-templates/instructions-viewer.tpl.html';
import fileTabTemplateDocsPane from './file-tab-templates/docs-pane.tpl.html';
import fileTabTemplateRunnerFrame from './file-tab-templates/runner-frame.tpl.html';
import fileTabTemplateWebPreview from './file-tab-templates/web-preview.tpl.html';
import fileTabTemplateWebPreviewConsole from './file-tab-templates/web-preview-console.tpl.html';
import fileTabTemplateIDEOpts from './file-tab-templates/ide-opts.tpl.html';
import fileTabTemplateChatAgent from './file-tab-templates/chat-agent-frame.tpl.html';
import './multi-file-layout.less';
import { tabsComponentModule } from '../tabs/tabs';
import { uiLayoutComponentModule } from '../ui-layout/ui-layout';
import { splitPath } from '../../utils/file-utils/file-utils';
import * as ArrayUtils from '../../utils/array-utils';
import { addUnique } from '../../utils/array-utils';
import { codemirrorComponentModule } from '../codemirror/codemirror';
import { keycodesModule } from '../../constants/keycodes';
import { codemirrorIdeOptsComponentModule } from '../codemirror/ide-opts/codemirror-ide-opts';
import { isFileEvent, stopEvent } from '../../utils/event-utils';
import { markdownEditorComponentModule } from '../markdown-editor/markdown-editor';
import { markdownComponentModule } from '../markdown/markdown';
import { runnerFrameComponentModule } from '../runner-frame/runner-frame';
import { relinkOnChangeComponentModule } from '../relink-on-change/relink-on-change';
import { webPreviewComponentModule } from '../web-preview/web-preview';
import {
  codemirrorOptionsComponentModule,
  cursorData,
} from '../codemirror/codemirror-options';
import { fileIconComponentModule } from '../file-icon/file-icon';
import { isString } from '../../utils/type-utils';
import { docsPaneComponentModule } from '../docs-pane/docs-pane';
import { memo } from '../../utils/function-utils';
import { scrollOverflowIndicatorModule } from '../scroll-overflow-indicator/scroll-overflow-indicator';
import { uiModernFrameComponentModule } from '../ui-modern-frame/ui-modern-frame';
import { isImageDataUrl } from '../../utils/file-utils/image-file-utils';

// can use template: 'key' to use built-in templates
const FILE_TAB_TEMPLATES = {
  ideOpts: fileTabTemplateIDEOpts,
  runnerFrame: fileTabTemplateRunnerFrame,
  imageViewer: fileTabTemplateImageViewer,
  markdownViewer: fileTabTemplateMarkdownViewer,
  instructionsViewer: fileTabTemplateInstructionsViewer,
  markdown: fileTabTemplateMarkdownEditor,
  code: fileTabTemplateCodeEditor,
  docsPane: fileTabTemplateDocsPane,
  webPreview: fileTabTemplateWebPreview,
  webPreviewConsole: fileTabTemplateWebPreviewConsole,
  chatagent: fileTabTemplateChatAgent,
};

let ftCounter = 0;

class FileTab {
  constructor({
    layout,
    file,
    tabset,
    disableWebPreview,
    tabindex,
    active = file.active || false,
  }) {
    this.layout = layout;
    this._workspace = layout.workspace;
    this.file = file;
    this.tabset = tabset;
    this.lastTabset = file.lastTabset || tabset;
    this.tabindex = tabindex;
    this.active = active;
    this.defaultActive = active;
    this.disableWebPreview = disableWebPreview;
    this.ftId = ++ftCounter;

    this.$update();
  }

  // only activates the tab itself, no focus
  activate() {
    this._active = true;
  }

  $update() {
    const file = this.file;
    this.filename = splitPath(file.path).filename;

    let template = file.template;
    let tabClass = '';
    if (!template) {
      if (isImageDataUrl(file && file.contents)) {
        template = fileTabTemplateImageViewer;
      } else if (file.$markdown) {
        if (file.$markdownReadOnly) {
          template = fileTabTemplateMarkdownViewer;
        } else {
          template = fileTabTemplateMarkdownEditor;
        }
      } else if (file.path) {
        template = fileTabTemplateCodeEditor;
        tabClass = 'code-tab';
      } // else provide your own!
    }
    if (FILE_TAB_TEMPLATES[template]) {
      template = FILE_TAB_TEMPLATES[template];
      if (file.template === 'runnerFrame') {
        tabClass = 'code-tab runner-frame';
      }
    }
    this.toolbarTemplate = this.layout.getToolbarTemplate({ file });
    this.template = template;
    this.tabClass = tabClass;

    if (this.layout.workspace && this._workspace !== this.layout.workspace) {
      this._workspace = this.layout.workspace;
      // force a relinking if the workspace changed
      this.relinkOnWorkspaceChange = Date.now();
    }
  }

  set active(active) {
    if (active !== this._active) {
      this._active = active;
      this.file.active = active;
      if (active && this.layout.initialized) {
        // enables the layout to handle focusing
        this.layout.select(this);
      }
    }
  }

  get active() {
    return this._active;
  }

  get label() {
    return this.file.label || this.filename;
  }

  get labelTemplate() {
    return this.file.labelTemplate;
  }

  get tooltip() {
    return this.file.tabTooltip || this.file.path;
  }

  set tabset(tabset) {
    this._tabset = tabset;
    this.file.tabset = tabset;
  }

  get tabset() {
    return this._tabset;
  }

  set lastTabset(tabset) {
    this._lastTabset = tabset;
    this.file.lastTabset = tabset;
  }

  get lastTabset() {
    return this._lastTabset;
  }

  set tabindex(tabindex) {
    this._tabindex = tabindex;
    this.file.tabindex = tabindex;
  }

  get tabindex() {
    return this._tabindex;
  }

  followLayoutEditing() {
    return this.file && !this.file.alwaysEdit;
  }

  get disabled() {
    return (
      (this.layout.disabled && this.followLayoutEditing()) ||
      (this.file && this.file.disabled)
    );
  }

  get readOnly() {
    return (
      this.expired ||
      (this.layout.readonly && this.followLayoutEditing()) ||
      (this.file && this.file.readOnly)
    );
  }

  get expired() {
    return this.layout.expired && this.file && this.file.expirable;
  }

  closeable() {
    return (
      this.file.closeable !== false &&
      this.layout &&
      this.layout.closeable !== false &&
      (!Array.isArray(this.layout.closeable) ||
        this.layout.closeable.includes(this.file))
    );
  }

  closeDisabled() {
    return this.file.closeable === 'disabled';
  }

  onClose() {}

  tabClasses() {
    let ret = this.tabClass || '';
    if (this.layout.draggingFileTab === this) {
      ret += ' dragged-file-tab';
    } else if (this.focused) {
      ret += ' focused-tab';
    }
    if (this.file.tabClasses) {
      ret += ' ' + this.file.tabClasses;
    }
    return ret;
  }
}

class FileTabPlaceholder {
  constructor(fileTab) {
    this.$placeholder = true;
    this.label = fileTab.label;
    this.labelTemplate = fileTab.labelTemplate;
    this.file = fileTab.file;
    this.closeable = fileTab.closeable;
    this.tabClasses = () => 'drag-and-drop-placeholder';
  }
}

export const multiFileLayoutComponentModule = angular
  .module('Q.components.multi-file-layout', [
    tabsComponentModule,
    uiLayoutComponentModule,
    codemirrorComponentModule,
    codemirrorOptionsComponentModule,
    codemirrorIdeOptsComponentModule,
    docsPaneComponentModule,
    markdownEditorComponentModule,
    markdownComponentModule,
    runnerFrameComponentModule,
    keycodesModule,
    relinkOnChangeComponentModule,
    webPreviewComponentModule,
    fileIconComponentModule,
    scrollOverflowIndicatorModule,
    uiModernFrameComponentModule,
  ])

  .component('multiFileLayout', {
    bindings: {
      openFiles: '=?',
      onRun: '&',
      onTestRun: '&',
      onFileChange: '&',
      onFilePaste: '&',
      onFileCopy: '&',
      onTabMoved: '&',
      onTabFocused: '&',

      disableTabMoving: '<',

      getToolbarTemplate: '&',

      parentController: '@',

      preserveOnClose: '<',

      // if true, any `expirable` code or markdown files will be marked as readOnly
      expired: '<',

      // if true, disables editing all code or markdown files
      disabled: '<',

      // if true, puts all code or markdown editors into readonly mode
      readonly: '<',

      // if set, enables syncing the current focus object
      cursor: '=?',

      // if false, don't close any files. If array, only allow closing those files
      closeable: '<',

      rememberUi: '<',

      // allows using LSP with our codemirror editors
      workspace: '<',

      // enables controlling the layout externally
      multiFileLayoutCtrl: '=?',
      // called after initialization is complete
      onInitialized: '&',

      // A template that can be used to inject non-tab actions into the tab bar
      tabBeforeActions: '<?',
      tabAfterActions: '<?',

      // Allows for force-disabling the web preview on load in tabs that use it
      disableWebPreview: '<?',
    },
    require: {
      multiFileChallengesCtrl: '^?linkMultiFileChallenges',
    },
    controller: 'MultiFileLayoutCtrl',
    template: multiFileLayoutTemplate,
  })

  .controller(
    'MultiFileLayoutCtrl',
    function MultiFileLayoutCtrl(
      $scope,
      $timeout,
      codemirrorOptions,
      KeyCodes,
      $document,
      $q,
      $log,
      $rootScope,
    ) {
      const ctrl = this;

      ctrl.tabsetTemplate = multiFileLayoutTabsetTemplate;

      const fileTabs = (ctrl.fileTabs = []);

      const tabsets = (ctrl.tabsets = {
        topLeft: [],
        topRight: [],
        bottomLeft: [],
        bottomRight: [],
      });

      const find = (ctrl.find = (file) => {
        if (file instanceof FileTab) {
          return file;
        } else if (angular.isString(file)) {
          const fieldName = file.replace(/\.[a-z]+$/i, '');
          return fileTabs.find(
            (ft) =>
              ft.file.path === file ||
              (ft.file.field && ft.file.field === fieldName),
          );
        } else {
          return fileTabs.find((ft) => ft.file === file);
        }
      });

      const setCursor = (ctrl.setCursor = (file, cursor) => {
        if (cursor) {
          const fileTab = find(file);
          if (fileTab && fileTab.setCursor) {
            fileTab.setCursor(cursor);
          }
        }
      });

      let focusTimer = null;
      const focus = (ctrl.focus = (file, cursor, force) => {
        // only focus on the first tab until initialized, and never if we're in the background
        if (focusTimer && !ctrl.initialized) {
          return;
        }

        const fileTab = (ctrl.activeFileTab = find(file));
        $timeout.cancel(focusTimer);
        focusTimer = $timeout(() => {
          focusTimer = null;
          ctrl.setFocusedTab(fileTab);
          fileTab.activate();
          if (force || $document[0].hasFocus()) {
            if (fileTab.focus) {
              fileTab.focus(cursor);
            }
            ctrl._onFileChange(file);
          } else {
            // just set the cursor on this tab now
            setCursor(file, cursor);
          }
        }, 100);
      });
      ctrl.chatAgentIframeRoute = (fileTab) => {
        // Build route with query parameters for authentication
        const params = new URLSearchParams();

        // Get data from fileTab.file.challenge and parent controller
        const challenge = fileTab && fileTab.file && fileTab.file.challenge;
        const solution = fileTab && fileTab.file && fileTab.file.solution;

        // Try to get from parent controller if available
        const parentCtrl = $scope.$parent && $scope.$parent.$ctrl;

        // Get challengeId from challenge (REQUIRED)
        if (challenge && challenge.id) {
          params.append('challengeId', challenge.id);
        } else if (
          parentCtrl &&
          parentCtrl.challenge &&
          parentCtrl.challenge.id
        ) {
          params.append('challengeId', parentCtrl.challenge.id);
        }

        // Get assessmentId from solution's assessmentResult (OPTIONAL - not available in preview mode)
        if (solution && solution.assessmentResult) {
          params.append('assessmentId', solution.assessmentResult.assessmentId);
        } else if (
          parentCtrl &&
          parentCtrl.solution &&
          parentCtrl.solution.assessmentResult
        ) {
          params.append(
            'assessmentId',
            parentCtrl.solution.assessmentResult.assessmentId,
          );
        } else if (
          parentCtrl &&
          parentCtrl.assessmentResult &&
          parentCtrl.assessmentResult.assessmentId
        ) {
          params.append(
            'assessmentId',
            parentCtrl.assessmentResult.assessmentId,
          );
        }
        // Note: In preview mode, assessmentId won't be available
        // Rails will handle this by looking for dry_run assessment_result

        // Get language from solution (OPTIONAL - for code challenges)
        if (solution && solution.language) {
          params.append('language', solution.language);
        } else if (
          parentCtrl &&
          parentCtrl.solution &&
          parentCtrl.solution.language
        ) {
          params.append('language', parentCtrl.solution.language);
        }

        // Get AI agent message from challenge (OPTIONAL - configurable message for candidates)
        if (challenge && challenge.aiAgentMessage) {
          params.append(
            'aiAgentMessage',
            encodeURIComponent(challenge.aiAgentMessage),
          );
        } else if (
          parentCtrl &&
          parentCtrl.challenge &&
          parentCtrl.challenge.aiAgentMessage
        ) {
          params.append(
            'aiAgentMessage',
            encodeURIComponent(parentCtrl.challenge.aiAgentMessage),
          );
        }

        // Determine if viewing as team member
        // If there's no assessmentId, it means we're in preview/edit mode (team member)
        // Candidates always have an assessmentId from their assessment result
        const hasAssessmentId = params.has('assessmentId');
        const isTeamMember = !hasAssessmentId;

        if (isTeamMember) {
          params.append('isTeamMember', 'true');
        }

        const route = `embed/agent-chat?${params.toString()}`;
        return route;
      };

      ctrl.broadcastToEmbeddedFrames = (payload) => {
        const iframes = document.querySelectorAll('iframe');
        iframes.forEach((iframe) => {
          try {
            if (iframe.contentWindow) {
              $log.debug('Sending message to iframe', payload, iframe);
              iframe.contentWindow.postMessage(payload, '*');
            }
          } catch (err) {
            $log.debug('Failed to postMessage to iframe', err);
          }
        });
      };

      // Feature 8: Code Selection Context for AI Chat
      // Listen for explicit "add to chat" events (via keyboard shortcut or context menu)
      ctrl.currentCodeSelection = null;

      $scope.$on('codemirror:add-to-chat', ($event, codeSelection) => {
        // Store current selection
        ctrl.currentCodeSelection = codeSelection;

        // Find chat agent iframe by searching for iframe with agent-chat in src
        // Using vanilla JS instead of jqLite filter (not supported)
        const iframes = document.querySelectorAll('iframe');
        let chatIframe = null;

        for (let i = 0; i < iframes.length; i++) {
          if (
            iframes[i].src &&
            iframes[i].src.indexOf('embed/agent-chat') !== -1
          ) {
            chatIframe = iframes[i];
            break;
          }
        }

        if (chatIframe && chatIframe.contentWindow) {
          // Send selection to Vue chat UI via postMessage
          chatIframe.contentWindow.postMessage(
            {
              type: 'CODE_SELECTION_UPDATE',
              selection: codeSelection,
            },
            '*', // In development, allow all origins. In production, should specify exact origin
          );
        }
      });

      // Listen for requests from iframe to get current selection
      const handlePostMessage = (event) => {
        if (event.data && event.data.type === 'REQUEST_CODE_SELECTION') {
          // Re-send current selection if available
          if (ctrl.currentCodeSelection) {
            // Find chat agent iframe using vanilla JS
            const iframes = document.querySelectorAll('iframe');
            let chatIframe = null;

            for (let i = 0; i < iframes.length; i++) {
              if (
                iframes[i].src &&
                iframes[i].src.indexOf('embed/agent-chat') !== -1
              ) {
                chatIframe = iframes[i];
                break;
              }
            }

            if (chatIframe && chatIframe.contentWindow) {
              chatIframe.contentWindow.postMessage(
                {
                  type: 'CODE_SELECTION_UPDATE',
                  selection: ctrl.currentCodeSelection,
                },
                '*',
              );
            }
          }
        }
      };

      window.addEventListener('message', handlePostMessage);

      // Cleanup on destroy
      $scope.$on('$destroy', () => {
        window.removeEventListener('message', handlePostMessage);
      });
      ctrl._onFileChange = (file, userInitiated) => {
        ctrl.onFileChange({ file, userInitiated });
      };

      ctrl.setFocusedTab = (fileTab) => {
        ctrl.activeFileTab = fileTab;
        ctrl.fileTabs.forEach((ft) => (ft.focused = ft === fileTab));

        if (!fileTab) {
          $log.error(
            "Invariant violation: expected fileTab to be defined but it wasn't",
          );
          return;
        }

        ctrl.onTabFocused({ file: fileTab.file });
      };

      ctrl.resetOpenFiles = () => {
        fileTabs.length = 0;
        angular.forEach(tabsets, (ts) => (ts.length = 0));
        ctrl.refresh();
      };

      // Open or select a file, moving focus over automatically
      ctrl.select = ctrl.open = (file, { activate = true, cursor } = {}) => {
        let fileTab = find(file);
        if (!fileTab) {
          fileTab = addFile(file, activate);
          ArrayUtils.addUnique(ctrl.openFiles, file);
        }
        if (activate) {
          fileTab.active = true;
          (tabsets[fileTab.tabset] || []).forEach((ft) => {
            ft.active = ft === fileTab;
          });
          focus(fileTab, cursor);
        } else {
          setCursor(file, cursor);
          ctrl._onFileChange(file);
        }
        return fileTab;
      };

      // Close a file
      ctrl.close = (file) => {
        const fileTab = find(file);
        if (fileTab) {
          $q.resolve(fileTab.onClose()).then(() => {
            ArrayUtils.remove(tabsets[fileTab.tabset], fileTab);
            ArrayUtils.remove(fileTabs, fileTab);
            ArrayUtils.remove(ctrl.openFiles, fileTab.file);
            fileTab.lastTabset = fileTab.tabset;
            if (!ctrl.preserveOnClose) {
              fileTab.tabset = null;
              fileTab.tabindex = null;
            }
            if (ctrl.initialized) {
              cleanEmptyTabsets();
              ctrl._onFileChange();
            }
          });
        }
      };

      ctrl.toggle = (file, open, activate) => {
        return open ? ctrl.open(file, { activate }) : ctrl.close(file);
      };

      // moves a file to a specific tabset & optional tab index
      // if the tab index is not provided, it defaults to the end of the tabset
      ctrl.move = (file, tabset, tabindex = Infinity) => {
        const fileTab = find(file);
        if (fileTab) {
          if (fileTab.$draggedIn) {
            fileTabs.push(fileTab);
            ArrayUtils.addUnique(ctrl.openFiles, fileTab.file);
            delete fileTab.$draggedIn;
          } else {
            ArrayUtils.remove(tabsets[fileTab.tabset], fileTab);
          }
          fileTab.tabset = tabset;
          tabsets[tabset].splice(tabindex, 0, fileTab);
          cleanEmptyTabsets();
          $timeout(() => {
            ctrl.select(fileTab);
            ctrl.onTabMoved(fileTab);
          });
        }
      };

      ctrl.onCursorActivity = (fileTab, cm) => {
        const file = fileTab.file;
        if (file.path) {
          ctrl.selections = file.selections = cm.listSelections();
          ctrl.cursor = file.cursor = cursorData(cm, {
            path: file.path,
            field: file.field,
          });
          $scope.$applyAsync(() => {
            ctrl._onFileChange(file, !cm.$nonUserInput);
          });
        } // TODO: handle non-path files?
      };

      ctrl.handlePaste = (fileTab, change) => {
        ctrl.onFilePaste({
          file: fileTab.file,
          change,
          pasted: change.text.join('\n'),
        });
      };

      ctrl.handleCopy = (fileTab, copied) => {
        ctrl.onFileCopy({
          file: fileTab.file,
          copied,
        });
      };

      ctrl.createCodeOpts = memo((fileTab) => {
        const filename = splitPath(fileTab.file.path).filename;
        const destroyFilenameWatcher = $scope.$watch(
          () => splitPath(fileTab.file.path).filename,
          (newVal) => {
            if (newVal !== filename) {
              ctrl.createCodeOpts.delete(fileTab);
            }
          },
        );
        const opts = codemirrorOptions.createOpts(filename);

        // Feature 8: Pass file info to CodeMirror for code selection context
        opts.file = fileTab.file; // Make file available to CodeMirror scope

        if (ctrl.workspace) {
          // add in the workspace gutter
          addUnique(opts.gutters, 'cmw-gutter');
        }
        opts.onLoad = function (cmEditor) {
          ['focus', 'mousedown', 'touchstart'].forEach((event) => {
            cmEditor.on(event, () => {
              $scope.$applyAsync(() => {
                ctrl.select(fileTab);
              });
            });
          });
          cmEditor.on('blur', () => {
            $scope.$applyAsync();
          });
          cmEditor.on('change', function (evt, change) {
            if (change.origin === 'paste') {
              ctrl.handlePaste(fileTab, change);
            }
          });
          const onCopy = () => {
            ctrl.handleCopy(fileTab, cmEditor.doc.getSelection('\n'));
          };
          cmEditor.on('copy', onCopy);
          cmEditor.on('cut', onCopy);

          const preventDefaultOnFiles = (instance, event) => {
            if (isFileEvent(event)) {
              // allow the parent component to handle file drop events
              event.codemirrorIgnore = true;
            }
          };

          cmEditor.on('drop', preventDefaultOnFiles);
          cmEditor.on('dragover', preventDefaultOnFiles);
          cmEditor.on('dragenter', preventDefaultOnFiles);
          cmEditor.on('dragleave', preventDefaultOnFiles);
          cmEditor.on('cursorActivity', (cm) => {
            ctrl.onCursorActivity(fileTab, cm);
          });
          // After user input, pause playback auto-follow briefly so reviewers can
          // inspect code without being pulled by incoming playback updates.
          const PAUSE_PLAYBACK_AUTOSCROLL_MS = 3000;
          // Generic "scroll" can also come from programmatic updates, so we only
          // trust it when a manual intent signal happened very recently.
          const MANUAL_SCROLL_INTENT_WINDOW_MS = 250;
          const markManualScrollIntent = (source = 'unknown') => {
            cmEditor.$playbackManualScrollIntentAt = Date.now();
            cmEditor.$playbackManualScrollIntentSource = source;
          };
          const pausePlaybackAutoScroll = (source = 'unknown') => {
            const pauseUntil = Date.now() + PAUSE_PLAYBACK_AUTOSCROLL_MS;
            cmEditor.$playbackUserScrollPauseUntil = pauseUntil;
            const scrollInfo = cmEditor.getScrollInfo();
            cmEditor.$playbackPausedScrollPosition = {
              left: scrollInfo.left,
              top: scrollInfo.top,
            };
            $log.debug(
              '[playback-scroll] user scroll pause set',
              source,
              fileTab.file.path || fileTab.file.id,
              pauseUntil,
            );
          };
          cmEditor.on('scroll', () => {
            if (cmEditor.$nonUserInput) return;
            const now = Date.now();
            const intentAt = cmEditor.$playbackManualScrollIntentAt || 0;
            if (now - intentAt <= MANUAL_SCROLL_INTENT_WINDOW_MS) {
              // Promote trusted scroll intent to a full auto-follow pause window.
              pausePlaybackAutoScroll(
                'scroll:' +
                  (cmEditor.$playbackManualScrollIntentSource || 'unknown'),
              );
            }
          });
          const scroller = cmEditor.getScrollerElement
            ? cmEditor.getScrollerElement()
            : null;
          if (scroller) {
            scroller.addEventListener(
              'wheel',
              () => {
                markManualScrollIntent('wheel');
                pausePlaybackAutoScroll('wheel');
              },
              {
                passive: true,
              },
            );
            scroller.addEventListener(
              'touchmove',
              () => {
                markManualScrollIntent('touchmove');
                pausePlaybackAutoScroll('touchmove');
              },
              {
                passive: true,
              },
            );
            scroller.addEventListener('keydown', (event) => {
              const keys = [
                'ArrowUp',
                'ArrowDown',
                'PageUp',
                'PageDown',
                'Home',
                'End',
                'Space',
              ];
              if (keys.includes(event.key)) {
                markManualScrollIntent('keydown:' + event.key);
                pausePlaybackAutoScroll('keydown:' + event.key);
              }
            });
          }

          cmEditor.addKeyMap({
            [KeyCodes.COMMAND_KEY + '-Enter']: ctrl.onRun,
            [KeyCodes.COMMAND_KEY + "-'"]: ctrl.onTestRun,
          });

          if (fileTab.file.cmOnLoad) {
            fileTab.file.cmOnLoad(cmEditor);
          }

          if (fileTab.file.cursor && fileTab.file.cursor.line) {
            // restore cursor
            cmEditor.$nonUserInput = true;
            cmEditor.setCursor(fileTab.file.cursor);
            cmEditor.$nonUserInput = false;
          }

          if (ctrl.workspace) {
            ctrl.workspace.openEditor(fileTab.file, cmEditor);
            fileTab.onClose = () => {
              ctrl.createCodeOpts.delete(fileTab);
              destroyFilenameWatcher();
              if (!ctrl.$destroyed && ctrl.workspace) {
                // ensure we save documents and properly close the editor
                return ctrl.workspace
                  .saveOpenFile(fileTab.file)
                  .then(() => ctrl.workspace.closeFile(fileTab.file));
              }
            };
          }
        };
        return opts;
      });

      function getTabindex(v) {
        return v.tabindex >= 0 ? v.tabindex : Infinity;
      }

      function addFile(file, activate) {
        const tabset =
          file.tabset || file.lastTabset || file.defaultTabset || 'topLeft';
        const fileTab = new FileTab({
          layout: ctrl,
          file,
          tabset,
          tabindex: getTabindex(file),
          active: activate || file.active,
          disableWebPreview: ctrl.disableWebPreview,
        });
        if (fileTab.file.id === 'runnerframe' && $rootScope.playbackToken) {
          fileTab.tabindex = 2;
          fileTab.focused = false;
          fileTab.active = false;
          fileTab.defaultActive = false;
        }
        if (fileTab.file.id === 'webpreview' && $rootScope.playbackToken) {
          fileTab.active = true;
          fileTab.focused = true;
        }
        fileTabs.push(fileTab);
        tabsets[tabset].push(fileTab);
        if (ctrl.initialized) {
          cleanEmptyTabsets();
        } else {
          // Handle initial sorting based on previous layout
          tabsets[tabset].sort((a, b) => getTabindex(a) - getTabindex(b));
        }
        if (ctrl.initialized && (activate || file.active)) {
          focus(fileTab);
        }
        return fileTab;
      }

      ctrl.classes = () => {
        return Object.keys(tabsets)
          .map((ts) => (tabsets[ts].length ? ts : 'not-' + ts))
          .concat(
            ctrl.draggingFileTab ? 'dragging-tab' : [],
            ctrl.openFiles.length === 1 ? 'single-tab-mode' : [],
            ctrl.disabledPlaceholder,
            $scope.$root.$ideDarkClass,
          )
          .join(' ');
      };

      ctrl.startDragging = (fileTabOrFile, event, external = true) => {
        let fileTab = fileTabOrFile;
        if (!(fileTabOrFile instanceof FileTab)) {
          fileTab = find(fileTabOrFile);
          if (!fileTab) {
            fileTab = new FileTab({
              layout: ctrl,
              file: fileTabOrFile,
              active: false,
            });
            // track that this is a temporary file tab
            fileTab.$draggedIn = true;
          }
        }

        ctrl.draggingFileTab = fileTab;
        ctrl.disabledPlaceholder = [
          fileTab.tabset === 'topLeft' &&
            ctrl.openFiles.length === 1 &&
            'topRight',
          fileTab.tabset === 'topLeft' &&
            ctrl.tabsets.topLeft.length === 1 &&
            'bottomLeft',
          fileTab.tabset === 'topRight' &&
            ctrl.tabsets.topRight.length === 1 &&
            'bottomRight',
        ]
          .filter(angular.identity)
          .map((v) => `disable-${v}`)
          .join(' ');

        if (!external) {
          const contents = fileTab.file.contents || fileTab.label;
          event.dataTransfer.setData('text/plain', contents);

          if (fileTab.filename && window.URL && window.File) {
            const data = URL.createObjectURL(
              new File([contents], fileTab.filename),
            );
            event.dataTransfer.setData(
              'DownloadUrl',
              `text/plain:${fileTab.filename}:${data}`,
            );
          }

          if (ctrl.onTabDragStart) {
            ctrl.onTabDragStart({ file: fileTab.file, event });
          }
        }
      };

      const _dropzones = [];
      ctrl.addDropZoneCleanup = (fn) => {
        ArrayUtils.addUnique(_dropzones, fn);
      };
      ctrl.removeDropZoneCleanup = (fn) => {
        ArrayUtils.remove(_dropzones, fn);
      };
      ctrl.stopDragging = (external = true) => {
        ctrl.draggingFileTab = null;
        _dropzones.forEach((fn) => {
          fn();
        });
        if (!external && ctrl.onTabDragStop) {
          ctrl.onTabDragStop();
        }
      };

      // Fallback: if the dragged tab is removed mid-drag, its own dragend
      // never fires and draggingFileTab is stuck on forever (QUAL-2326).
      const clearDraggingFileTab = () => {
        ctrl.stopDragging(true);
      };
      $document.on('dragend drop', clearDraggingFileTab);
      $scope.$on('$destroy', () => {
        $document.off('dragend drop', clearDraggingFileTab);
      });

      // ensures we don't have tabs only on the bottom or the right
      function cleanEmptyTabsets() {
        const _moveTabs = (from, to) => {
          tabsets[to].push(...tabsets[from]);
          tabsets[from].length = 0;
          tabsets[to].forEach((ft, idx) => {
            ft.tabset = to;
            ft.tabindex = idx;
          });
        };
        if (tabsets.topRight.length === 0) {
          // move bottomRight up
          _moveTabs('bottomRight', 'topRight');
        }
        if (tabsets.topLeft.length === 0 && !$rootScope.playbackToken) {
          if (tabsets.bottomLeft.length === 0) {
            // move everything to the left
            _moveTabs('topRight', 'topLeft');
            _moveTabs('bottomRight', 'bottomLeft');
          } else {
            // just move bottomLeft up
            _moveTabs('bottomLeft', 'topLeft');
          }
        }
        // update tabindexes if necessary
        angular.forEach(tabsets, (tabset) => {
          tabset.forEach((ft, idx) => {
            ft.tabindex = idx;
          });
        });
      }

      ctrl.$onInit = () => {
        ctrl.scopeVars = (fileTab) => {
          const ctrlName = ctrl.parentController || '$ctrl';
          return {
            fileTab: fileTab,
            mflCtrl: ctrl,
            [ctrlName]: $scope.$parent[ctrlName],
          };
        };
        if (!Array.isArray(ctrl.openFiles)) {
          ctrl.openFiles = [];
        }
        ctrl.openFiles.forEach((file) => {
          addFile(file);
        });
        ctrl.multiFileLayoutCtrl = ctrl;

        ctrl.refresh = () => {
          // reinitialize
          ctrl.initialized = false;
          // check to see if anything disappeared & close it
          const missingFilesTabs = fileTabs.filter(
            (ft) => !ctrl.openFiles.includes(ft.file),
          );
          if (ctrl.preserveOnClose) {
            missingFilesTabs.forEach(ctrl.close);
          } else {
            missingFilesTabs.forEach((missingFileTab) => {
              // try to map it to an open file that might have been replaced, but isn't already mapped
              let matchedFile = ctrl.openFiles.find(
                (f) =>
                  fileTabs.every((ft) => f !== ft.file) &&
                  ((f.id && f.id === missingFileTab.file.id) ||
                    angular.equals(f, missingFileTab.file)),
              );
              if (matchedFile) {
                // fix fileTab (it'll be updated below)
                missingFileTab.file = matchedFile;
              } else {
                // close no longer open file tab
                ctrl.close(missingFileTab);
              }
            });
          }
          // update all existing files
          fileTabs.forEach((ft) => {
            ft.$update();
          });
          // open any newly opened files
          ctrl.openFiles
            .filter((file) => !fileTabs.find((ft) => ft.file === file))
            .forEach((file) => {
              addFile(file);
            });
          // cleanup
          cleanEmptyTabsets();
          ctrl._onFileChange();
          $timeout(() => {
            ctrl.initialized = true;
          });

          if (ctrl.multiFileChallengesCtrl) {
            ctrl.multiFileChallengesCtrl.linkLayout(ctrl);
          }
        };

        if (ctrl.multiFileChallengesCtrl) {
          ctrl.multiFileChallengesCtrl.linkLayout(ctrl);
        }

        $timeout(() => {
          ctrl.initialized = true;
          ctrl.onInitialized();
          $scope.$watch(
            () => ctrl.openFiles.map((f) => f.id + f.path + f.label).join('\n'),
            ctrl.refresh,
          );
        });

        const rui = (ctrl.rui = {
          vert: false,
          left: false,
          right: false,
        });
        if (ctrl.rememberUi) {
          const opts = angular.extend(
            {
              id: '',
              scope: 'url',
              session: true,
            },
            isString(ctrl.rememberUi)
              ? { id: ctrl.rememberUi }
              : ctrl.rememberUi,
          );
          rui.vert = angular.extend({}, opts, {
            id: opts.id + '--layout-vert-splitter',
          });
          rui.left = angular.extend({}, opts, {
            id: opts.id + '--layout-left-splitter',
          });
          rui.right = angular.extend({}, opts, {
            id: opts.id + '--layout-right-splitter',
          });
        }
      };
    },
  )

  .directive('multiFileDropZone', function ($document, $log) {
    return {
      restrict: 'A',
      require: '^multiFileLayout',
      link(scope, elem, attr, multiFileLayoutCtrl) {
        if (multiFileLayoutCtrl.disableTabMoving) return;
        let placeholder;
        let tabindex;
        let dragEnterCounter = 0;
        let nextScroll = 0;
        const cleanupPlaceholder = () => {
          dragEnterCounter = 0;
          ArrayUtils.remove(
            multiFileLayoutCtrl.tabsets[attr.multiFileDropZone],
            placeholder,
          );
          placeholder = null;
          scope.$apply();
        };
        multiFileLayoutCtrl.addDropZoneCleanup(cleanupPlaceholder);
        scope.$on('$destroy', () => {
          multiFileLayoutCtrl.removeDropZoneCleanup(cleanupPlaceholder);
        });

        elem
          .on('dragenter', () => {
            dragEnterCounter++;
            elem.addClass('drag-over');
            tabindex = undefined;
          })
          .on('dragover', (event) => {
            if (multiFileLayoutCtrl.draggingFileTab) {
              stopEvent(event);

              event.dataTransfer.dropEffect = 'move';
              let newTabindex = Infinity;

              if (!$document[0].elementsFromPoint) {
                $log.error("Browser doesn't support elementsFromPoint");
                return;
              }

              const dragOverElements = $document[0].elementsFromPoint(
                event.clientX,
                event.clientY,
              );

              // see if we're over a tab
              const tabOver = dragOverElements.find(
                (el) =>
                  el.nodeName === 'LI' &&
                  el.classList.contains('multi-file-layout-tab'),
              );
              if (tabOver) {
                // calculate tabindex of hovering node
                newTabindex = 0;
                const placeholderTab = tabOver.parentElement.querySelector(
                  'li.drag-and-drop-placeholder',
                );
                if (placeholderTab) {
                  const tabOverRect = tabOver.getBoundingClientRect();
                  const placeholderTabRect =
                    placeholderTab.getBoundingClientRect();
                  if (
                    event.clientX - tabOverRect.x >
                    placeholderTabRect.width
                  ) {
                    // prevent annoying flipping between different sized tabs
                    newTabindex += 1;
                  }
                }
                let child = tabOver;
                while ((child = child.previousElementSibling) !== null) {
                  newTabindex += 1;
                }
              }

              // see if we need to scroll the tabs
              const tabScroller = dragOverElements.find(
                (el) =>
                  el.nodeName === 'A' &&
                  (el.classList.contains('tab-scroller-left') ||
                    el.classList.contains('tab-scroller-right')),
              );
              if (tabScroller && Date.now() > nextScroll) {
                angular.element(tabScroller).triggerHandler('click');
                // only scroll a tab every .5 seconds
                nextScroll = Date.now() + 500;
              }

              const targetTabset = attr.multiFileDropZone;
              const targetTabsetArray =
                multiFileLayoutCtrl.tabsets[targetTabset];

              if (!placeholder) {
                if (targetTabsetArray.length !== 0) {
                  placeholder = new FileTabPlaceholder(
                    multiFileLayoutCtrl.draggingFileTab,
                  );
                  targetTabsetArray.splice(newTabindex, 0, placeholder);
                  scope.$apply();
                }
              } else if (newTabindex !== tabindex) {
                ArrayUtils.remove(targetTabsetArray, placeholder);
                targetTabsetArray.splice(newTabindex, 0, placeholder);
                scope.$apply();
              }
              tabindex = newTabindex;
            }
          })
          .on('dragleave', () => {
            dragEnterCounter--;
            if (dragEnterCounter < 1) {
              elem.removeClass('drag-over');
              cleanupPlaceholder();
            }
          })
          .on('drop', (event) => {
            cleanupPlaceholder();
            elem.removeClass('drag-over');
            if (multiFileLayoutCtrl.draggingFileTab) {
              stopEvent(event);
              scope.$applyAsync(() => {
                const targetTabset =
                  multiFileLayoutCtrl.tabsets[attr.multiFileDropZone];
                const existingTabindex = targetTabset.indexOf(
                  multiFileLayoutCtrl.draggingFileTab,
                );
                if (existingTabindex > -1 && existingTabindex < tabindex) {
                  // adjust final tab index if we're just moving past the current location
                  // (this is due to the placeholder element)
                  tabindex--;
                }
                multiFileLayoutCtrl.move(
                  multiFileLayoutCtrl.draggingFileTab,
                  attr.multiFileDropZone,
                  tabindex,
                );
                multiFileLayoutCtrl.draggingFileTab = null;
              });
            }
          });
      },
    };
  })

  .directive('multiFileDraggableTab', function ($timeout) {
    let counter = 0;
    return {
      restrict: 'A',
      link(scope, elem, attr) {
        if (scope.$eval(attr.multiFileDraggableTab) === false) return;
        elem
          .attr('id', 'draggable-tab-' + ++counter)
          .attr('draggable', true)
          .on('dragstart', (event) => {
            scope.$apply(() => {
              const fileTab = scope.$eval(attr.multiFileDraggableTab);
              const layout = fileTab.layout;
              layout.startDragging(fileTab, event, false);
            });
          })
          .on('dragend', () => {
            $timeout(() => {
              const fileTab = scope.$eval(attr.multiFileDraggableTab);
              fileTab.layout.stopDragging(false);
            });
          });
      },
    };
  })

  .directive('multiFileContents', function ($timeout, $log) {
    return {
      restrict: 'A',
      link(scope, elem, attr) {
        const fileTab = scope.$eval(attr.multiFileContents);
        if (fileTab) {
          elem.on('click', () => {
            scope.$apply(() => {
              // ensures we highlight the tab if we have focus
              fileTab.layout.setFocusedTab(fileTab);
            });
          });

          let lastFocused;

          elem.on('focusin', (event) => {
            lastFocused = event.target;
            if (lastFocused.matches('.CodeMirror textarea')) {
              // change last focused to parent CodeMirror node, rather than it's textarea input node
              // Otherwise we can't set the cursor
              while (!lastFocused.matches('.CodeMirror')) {
                lastFocused = lastFocused.parentElement;
              }
            }
          });

          fileTab.setCursor = (cursor, cm) => {
            const isPlayback = !!fileTab.file.$reviewMode;
            if (!cursor && !isPlayback) return;

            if (!cm) {
              cm = elem[0].querySelector('.CodeMirror');
              if (!cm) return;
            }
            cm.$nonUserInput = true;
            if (cm.CodeMirror) cm = cm.CodeMirror;

            let shouldScrollIntoView = true;
            let playbackScrollTarget = null;
            let isUserScrollPaused = false;

            if (isPlayback) {
              const lastLine = cm.lastLine();
              const lastLineCh = cm.getLine(lastLine).length;
              // Prefer server/playback cursor (where the edit happened), fallback
              // to file end when cursor data is unavailable.
              const playbackCursor =
                cursor && angular.isNumber(cursor.line)
                  ? {
                      line: cursor.line,
                      ch: angular.isNumber(cursor.ch) ? cursor.ch : 0,
                    }
                  : {
                      line: lastLine,
                      ch: lastLineCh,
                    };
              const previousPlaybackState = cm.$playbackViewportState;
              const now = Date.now();
              isUserScrollPaused =
                now < (cm.$playbackUserScrollPauseUntil || 0);
              const SCROLL_INTERVAL_MS = 1200;
              const SCROLL_LINE_BATCH = 3;
              const SCROLL_CHAR_BATCH = 120;

              if (!isUserScrollPaused) {
                cm.$playbackPausedScrollPosition = null;
                const beforeCursorScroll = cm.getScrollInfo();
                // Keep cursor synced to playback progress without changing viewport.
                cm.setCursor(playbackCursor);
                cm.scrollTo(beforeCursorScroll.left, beforeCursorScroll.top);
              } else {
                $log.debug(
                  '[playback-scroll] skip cursor sync while user pause active',
                  fileTab.file.path || fileTab.file.id,
                  cm.$playbackUserScrollPauseUntil,
                );
                if (cm.$playbackPausedScrollPosition) {
                  cm.scrollTo(
                    cm.$playbackPausedScrollPosition.left,
                    cm.$playbackPausedScrollPosition.top,
                  );
                }
              }

              shouldScrollIntoView = false;
              if (previousPlaybackState) {
                const hasNewLines = lastLine > previousPlaybackState.lastLine;
                const hasNewCharsOnLastLine =
                  lastLine === previousPlaybackState.lastLine &&
                  lastLineCh > previousPlaybackState.lastLineCh;
                // Cursor movement can indicate edits in-place even when file length
                // did not change (e.g., middle-of-file playback edits).
                const hasCursorProgress =
                  playbackCursor.line !== previousPlaybackState.cursorLine ||
                  playbackCursor.ch !== previousPlaybackState.cursorCh;

                if (hasNewLines || hasNewCharsOnLastLine || hasCursorProgress) {
                  const lineDelta = hasNewLines
                    ? lastLine - previousPlaybackState.lastLine
                    : hasCursorProgress
                      ? Math.abs(
                          playbackCursor.line -
                            previousPlaybackState.cursorLine,
                        )
                      : 0;
                  const charDelta = hasNewCharsOnLastLine
                    ? lastLineCh - previousPlaybackState.lastLineCh
                    : hasCursorProgress
                      ? Math.abs(
                          playbackCursor.ch - previousPlaybackState.cursorCh,
                        )
                      : 0;

                  const pendingLines =
                    (previousPlaybackState.pendingLines || 0) + lineDelta;
                  const pendingChars =
                    (previousPlaybackState.pendingChars || 0) + charDelta;
                  const timeSinceLastScroll =
                    now -
                    (previousPlaybackState.lastScrollAt ||
                      previousPlaybackState.startedAt ||
                      now);
                  const shouldBatchScroll =
                    (pendingLines >= SCROLL_LINE_BATCH ||
                      pendingChars >= SCROLL_CHAR_BATCH ||
                      timeSinceLastScroll >= SCROLL_INTERVAL_MS) &&
                    !isUserScrollPaused;

                  if (shouldBatchScroll) {
                    // Only auto-scroll if the editing area is near the
                    // current viewport. This prevents yanking the user
                    // back when they've manually scrolled away to read
                    // other code. They "opt back in" naturally by
                    // scrolling back near the editing area.
                    const scrollInfo = cm.getScrollInfo();
                    const bottomVisibleLine = cm.lineAtHeight(
                      scrollInfo.top + scrollInfo.clientHeight,
                      'local',
                    );
                    const NEAR_VIEWPORT_LINES = 30;
                    if (lastLine <= bottomVisibleLine + NEAR_VIEWPORT_LINES) {
                      shouldScrollIntoView = true;
                      // Follow the actual edit location, not always file end.
                      playbackScrollTarget = {
                        line: playbackCursor.line,
                        ch: playbackCursor.ch,
                      };
                    }
                    cm.$playbackViewportState = {
                      lastLine,
                      lastLineCh,
                      cursorLine: playbackCursor.line,
                      cursorCh: playbackCursor.ch,
                      pendingLines: 0,
                      pendingChars: 0,
                      lastScrollAt: now,
                      startedAt: previousPlaybackState.startedAt || now,
                    };
                  } else {
                    cm.$playbackViewportState = {
                      lastLine,
                      lastLineCh,
                      cursorLine: playbackCursor.line,
                      cursorCh: playbackCursor.ch,
                      pendingLines,
                      pendingChars,
                      lastScrollAt: previousPlaybackState.lastScrollAt || now,
                      startedAt: previousPlaybackState.startedAt || now,
                    };
                  }
                } else {
                  cm.$playbackViewportState = {
                    lastLine,
                    lastLineCh,
                    cursorLine: playbackCursor.line,
                    cursorCh: playbackCursor.ch,
                    pendingLines: previousPlaybackState.pendingLines || 0,
                    pendingChars: previousPlaybackState.pendingChars || 0,
                    lastScrollAt: previousPlaybackState.lastScrollAt || now,
                    startedAt: previousPlaybackState.startedAt || now,
                  };
                }
              } else {
                cm.$playbackViewportState = {
                  lastLine,
                  lastLineCh,
                  cursorLine: playbackCursor.line,
                  cursorCh: playbackCursor.ch,
                  pendingLines: 0,
                  pendingChars: 0,
                  lastScrollAt: now,
                  startedAt: now,
                };
              }
            } else if (cursor.line !== null) {
              cm.$playbackViewportState = null;
              cm.setCursor(cursor);
            } else if (cursor.head && cursor.anchor) {
              cm.$playbackViewportState = null;
              cm.setSelection(cursor.anchor, cursor.head);
            } else if (cursor[0] && cursor[0].head && cursor[0].anchor) {
              cm.$playbackViewportState = null;
              cm.setSelections(cursor);
            }
            if (
              isPlayback &&
              isUserScrollPaused &&
              cm.$playbackPausedScrollPosition
            ) {
              shouldScrollIntoView = false;
              cm.scrollTo(
                cm.$playbackPausedScrollPosition.left,
                cm.$playbackPausedScrollPosition.top,
              );
            }
            if (shouldScrollIntoView) {
              const now = Date.now();
              if (now < (cm.$playbackUserScrollPauseUntil || 0)) {
                $log.debug(
                  '[playback-scroll] blocked autoscroll due to active user pause',
                  fileTab.file.path || fileTab.file.id,
                  cm.$playbackUserScrollPauseUntil,
                );
                shouldScrollIntoView = false;
              }
            }
            if (shouldScrollIntoView) {
              if (playbackScrollTarget) {
                const scrollInfo = cm.getScrollInfo();
                const topVisibleLine = cm.lineAtHeight(scrollInfo.top, 'local');
                const bottomVisibleLine = cm.lineAtHeight(
                  scrollInfo.top + scrollInfo.clientHeight,
                  'local',
                );
                // Avoid redundant motion: if target is already visible, don't scroll.
                if (
                  playbackScrollTarget.line >= topVisibleLine &&
                  playbackScrollTarget.line <= bottomVisibleLine
                ) {
                  shouldScrollIntoView = false;
                }
              }
            }
            if (shouldScrollIntoView) {
              if (playbackScrollTarget) {
                $log.debug(
                  '[playback-scroll] autoscroll to target',
                  fileTab.file.path || fileTab.file.id,
                  playbackScrollTarget,
                );
                cm.scrollIntoView(playbackScrollTarget, 80);
              } else {
                cm.scrollIntoView(null);
              }
            }
            cm.$nonUserInput = false;
          };

          fileTab.focus = (cursor) => {
            fileTab.layout.setFocusedTab(fileTab);
            $timeout(
              () => {
                let toFocus;
                if (
                  lastFocused &&
                  lastFocused.focus &&
                  angular.element(lastFocused).isVisible('basic')
                ) {
                  toFocus = lastFocused;
                } else {
                  toFocus = Array.from(
                    elem[0].querySelectorAll(
                      '.CodeMirror,input,textarea,select,a,button',
                    ),
                  ).find(
                    (input) =>
                      (input.CodeMirror || input.focus) &&
                      angular.element(input).isVisible(),
                  );
                  if (!toFocus && elem.focus) {
                    toFocus = elem;
                  }
                }
                if (toFocus) {
                  const cm = toFocus.CodeMirror;
                  if (cm) {
                    const isPlayback = !!fileTab.file.$reviewMode;
                    let focusScrollInfo = null;
                    if (isPlayback) {
                      focusScrollInfo = cm.getScrollInfo();
                    }
                    cm.focus();
                    if (focusScrollInfo) {
                      cm.scrollTo(focusScrollInfo.left, focusScrollInfo.top);
                    }
                    fileTab.setCursor(cursor, cm);
                  } else {
                    toFocus.focus({ preventScroll: true });
                  }
                } else {
                  fileTab.setCursor(cursor);
                }
              },
              100,
              false,
            );
          };
          fileTab.focus.contents = elem;
          scope.$on('$destroy', () => {
            if (fileTab.focus.contents === elem) {
              fileTab.focus = null;
            }
          });
        }
      },
    };
  });
